import React, { useState, useMemo, useRef, useEffect } from "react";
import {
  Box,
  TextField,
  FormHelperText,
  Select,
  MenuItem,
  Typography,
  Divider,
  ListSubheader,
  InputAdornment,
  useTheme,
  alpha,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useTranslate } from "@refinedev/core";
import {
  parsePhoneNumber,
  CountryCode,
  getCountryCallingCode,
} from "libphonenumber-js";
import { CountryData, getAllCountries, PRIORITY_COUNTRY_COUNT } from "../common/country-data";

interface CountryPhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  error?: boolean;
  helperText?: string;
  required?: boolean;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
}

/**
 * Normalize text for search by removing accents and converting to lowercase
 */
const normalizeForSearch = (text: string): string => {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
};

/**
 * Check if a country matches the search query
 */
const matchesSearch = (country: CountryData, query: string): boolean => {
  if (!query) return true;
  const normalizedQuery = normalizeForSearch(query);
  return (
    country.searchTerms.includes(normalizedQuery) ||
    normalizeForSearch(country.name).includes(normalizedQuery)
  );
};

export const CountryPhoneInput: React.FC<CountryPhoneInputProps> = ({
  value,
  onChange,
  onBlur,
  error = false,
  helperText = "",
  required = false,
  label,
  placeholder = "6 12 34 56 78",
  disabled = false,
}) => {
  const theme = useTheme();
  const translate = useTranslate();
  const resolvedLabel = label ?? translate('phone.label');
  const countries = useMemo(() => getAllCountries(), []);

  // State for selected country, phone number, and search
  const [selectedCountry, setSelectedCountry] = useState<CountryCode>("FR");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Focus search input when menu opens
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Get selected country data
  const selectedCountryData = useMemo(
    () => countries.find((c) => c.code === selectedCountry) || countries[0],
    [countries, selectedCountry]
  );

  // Filter countries based on search query
  const filteredCountries = useMemo(() => {
    if (!searchQuery) return countries;
    return countries.filter((country) => matchesSearch(country, searchQuery));
  }, [countries, searchQuery]);

  // Initialize values from the value prop if it exists
  React.useEffect(() => {
    if (value && value.startsWith("+")) {
      try {
        const parsed = parsePhoneNumber(value);
        if (parsed) {
          setSelectedCountry(parsed.country as CountryCode);
          setPhoneNumber(parsed.nationalNumber);
        }
      } catch {
        // If parsing fails, keep default values
      }
    }
  }, []);

  // Handle country change
  const handleCountryChange = (newCountryCode: CountryCode) => {
    setSelectedCountry(newCountryCode);

    // Update the full value
    if (phoneNumber) {
      const dialCode = getCountryCallingCode(newCountryCode);
      const cleanNumber = phoneNumber.replace(/^0+/, ""); // Remove leading zeros
      onChange(`+${dialCode}${cleanNumber}`);
    }
  };

  // Handle phone number change
  const handlePhoneChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target.value;
    // Keep only digits and spaces
    const cleaned = input.replace(/[^\d\s]/g, "");
    setPhoneNumber(cleaned);

    // Update the full value
    const dialCode = getCountryCallingCode(selectedCountry);
    const cleanNumber = cleaned.replace(/\s/g, "").replace(/^0+/, ""); // Remove spaces and leading zeros

    if (cleanNumber) {
      onChange(`+${dialCode}${cleanNumber}`);
    } else {
      onChange("");
    }
  };

  // Reset search when menu closes
  const handleClose = () => {
    setIsOpen(false);
    setSearchQuery("");
  };

  const handleOpen = () => {
    setIsOpen(true);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ display: "flex", gap: 1 }}>
        {/* Country selector with search */}
        <Select
          value={selectedCountry}
          onChange={(e) => handleCountryChange(e.target.value as CountryCode)}
          onOpen={handleOpen}
          onClose={handleClose}
          open={isOpen}
          disabled={disabled}
          error={error}
          sx={{
            minWidth: 130,
            maxWidth: 130,
            "& .MuiSelect-select": {
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            },
          }}
          MenuProps={{
            PaperProps: {
              sx: { maxHeight: 400, minWidth: 320 },
            },
            autoFocus: false,
          }}
          renderValue={() => (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <span style={{ fontSize: "1.4em" }}>{selectedCountryData.flag}</span>
              <span>{selectedCountryData.dialCode}</span>
            </Box>
          )}
        >
          {/* Search field at the top */}
          <ListSubheader sx={{ bgcolor: "background.paper", pt: 1, pb: 1 }}>
            <TextField
              size="small"
              inputRef={searchInputRef}
              placeholder={translate('phone.searchCountry')}
              fullWidth
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                e.stopPropagation();
                // Select first result on Enter
                if (e.key === "Enter" && filteredCountries.length > 0) {
                  handleCountryChange(filteredCountries[0].code);
                  handleClose();
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </ListSubheader>

          {/* Priority countries (France) */}
          {filteredCountries.slice(0, PRIORITY_COUNTRY_COUNT).map((country, index) => (
            <MenuItem
              key={country.code}
              value={country.code}
              sx={index === 0 && searchQuery ? { bgcolor: "action.hover" } : undefined}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, width: "100%" }}>
                <span style={{ fontSize: "1.4em", minWidth: "1.5em" }}>{country.flag}</span>
                <Typography
                  variant="body2"
                  sx={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                >
                  {country.name}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: alpha(theme.palette.text.primary, 0.6), fontWeight: 500, flexShrink: 0 }}
                >
                  {country.dialCode}
                </Typography>
              </Box>
            </MenuItem>
          ))}

          {/* Divider after priority countries */}
          {filteredCountries.length > 0 &&
            filteredCountries[0]?.code === "FR" &&
            filteredCountries.length > PRIORITY_COUNTRY_COUNT && (
              <Divider sx={{ my: 0.5 }} />
            )}

          {/* Other countries */}
          {filteredCountries.slice(PRIORITY_COUNTRY_COUNT).map((country, index) => {
            const isFirstResult = searchQuery && index === 0 && filteredCountries.slice(0, PRIORITY_COUNTRY_COUNT).length === 0;
            return (
              <MenuItem
                key={country.code}
                value={country.code}
                sx={isFirstResult ? { bgcolor: "action.hover" } : undefined}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, width: "100%" }}>
                  <span style={{ fontSize: "1.4em", minWidth: "1.5em" }}>{country.flag}</span>
                  <Typography
                    variant="body2"
                    sx={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                  >
                    {country.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: alpha(theme.palette.text.primary, 0.6), fontWeight: 500, flexShrink: 0 }}
                  >
                    {country.dialCode}
                  </Typography>
                </Box>
              </MenuItem>
            );
          })}

          {/* No results message */}
          {filteredCountries.length === 0 && (
            <MenuItem disabled>
              <Typography variant="body2" color="text.secondary">
                {translate('phone.noCountryFound')}
              </Typography>
            </MenuItem>
          )}
        </Select>

        {/* Phone number input */}
        <TextField
          fullWidth
          required={required}
          label={resolvedLabel}
          value={phoneNumber}
          onChange={handlePhoneChange}
          onBlur={onBlur}
          error={error}
          placeholder={placeholder}
          disabled={disabled}
        />
      </Box>

      {/* Helper text or error message */}
      {helperText && (
        <FormHelperText error={error} sx={{ mx: "14px", mt: "3px" }}>
          {helperText}
        </FormHelperText>
      )}
    </Box>
  );
};
